<!-- @format -->
<script lang="ts">
  import { onMount } from "svelte";
  import { activeTab } from "~/theme/demo2025/stores/activeTab";
  import { getStoresContext } from "~/stores";
  import { writable } from "svelte/store";
  import type { StoresKey } from "~/stores";

  // Define a minimal type for navigation menu items with Obj_i18n type
  type Obj_i18n<T> = { [locale: string]: T };
  
  type Tab = {
    id: number;
    link: string;
    url?: string;
    name_i18n: Obj_i18n<string>;
    link_i18n?: Obj_i18n<string>;
  };

  type TabStyles = {
    activeTabColor: string;
    hoverTabColor: string;
    tabBorderColor: string;
    tabFontColor: string;
    activeTabFontColor: string;
    hoverTabFontColor: string;
    tabFontSize: string;
    tabHorizontalPadding: string;
    tabVerticalPadding: string;
    tabGap: string;
    tabBorderRadius: string;
    tabBorderWidth: string;
    tabBorderStyle: string;
    useBorderedButton: boolean;
    numberOfColumns?: number;
  };

  export let request_store_key: StoresKey;
  let _class;
  export { _class as class };
  export let tabs: Tab[] = [];
  export let current: string;
  export let initialActiveTab: number | null; // Allow null for no tab selected
  export let aboutPagePath: string;
  export let currentLocale: string;
  export let tabTitles: Record<number, string> = {};
  export let tabStyles: TabStyles;
  export let serverActiveTabId: number | null = null;
  export let serverActiveTabPath: string | null = null;

  // Create a reactive store for current path
  const currentPath = writable(current);
  $: $currentPath = current;
  
  const { reverse_router, i18nOf, T } = getStoresContext(request_store_key);

  // Mounted state for transition effects
  let isMounted = false;

  // Set active tab
  $: if (serverActiveTabId) {
    activeTab.set(serverActiveTabId);
  }

  // Set current path
  $: if (serverActiveTabPath) {
    $currentPath = serverActiveTabPath;
  }

  onMount(() => {
    isMounted = true;
  });

  function getTabTitle(tab: Tab): string {
    if (!tab) return $T('about-us.navigation-tabs-placeholder');
    return tabTitles[tab.id] || i18nOf(tab.name_i18n) || '';
  }

  // Get the current tab
  $: currentTab = tabs.find((tab) => tab.link === $currentPath);
  $: currentTabTitle = currentTab ? getTabTitle(currentTab) : $T('about-us.navigation-tabs-placeholder');

  function isActive(tab: Tab) {
    // If we're on the about page and no tab is explicitly selected
    if (current === aboutPagePath && !serverActiveTabId) {
      return false;
    }
    
    // Use server-provided active tab ID
    if (serverActiveTabId === tab.id) return true;
    
    return false;
  }
  
  let isOpen = false;
  let dropdownRef: HTMLElement;

  function toggleDropdown() {
    isOpen = !isOpen;
  }

  function selectOption(tab: Tab) {
    let path = "";
    if (tab.link) {
      path = $reverse_router.routePage(tab.link);
    } else if (tab.url) {
      path = tab.url;
    }
    
    // Navigate to the page
    window.location.pathname = path;
    isOpen = false;
  }

  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
      isOpen = false;
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (["Enter", "Space", " "].includes(event.key)) {
      event.preventDefault();
      toggleDropdown();
    } else if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      isOpen = false;
    }
  }

  function handleOptionKeyDown(event: KeyboardEvent, tab: Tab) {
    if (["Enter", "Space", " "].includes(event.key)) {
      event.preventDefault();
      selectOption(tab);
    }
  }
</script>

<div class="tabs-container {isMounted ? 'is-mounted' : ''}" style="
  --active-tab-color: {tabStyles.activeTabColor};
  --hover-tab-color: {tabStyles.hoverTabColor};
  --tab-border-color: {tabStyles.tabBorderColor};
  --tab-font-color: {tabStyles.tabFontColor};
  --active-tab-font-color: {tabStyles.activeTabFontColor};
  --hover-tab-font-color: {tabStyles.hoverTabFontColor};
  --tab-font-size: {tabStyles.tabFontSize};
  --tab-h-padding: {tabStyles.tabHorizontalPadding};
  --tab-v-padding: {tabStyles.tabVerticalPadding};
  --tab-gap: {tabStyles.tabGap};
  --tab-border-radius: {tabStyles.tabBorderRadius};
  --tab-border-width: {tabStyles.tabBorderWidth};
  --tab-border-style: {tabStyles.tabBorderStyle};
  --number-of-columns: {tabStyles.numberOfColumns || 6};
">
  <!-- Dropdown for smaller screens -->
  <div class="custom-dropdown-container" bind:this={dropdownRef}>
    <button
      class="dropdown-trigger"
      on:click={toggleDropdown}
      on:keydown={handleKeyDown}
      aria-label="Select navigation tab"
      tabindex="0">
      <span class="dropdown-selected">
        {currentTabTitle}
      </span>
      <svg
        class={`dropdown-icon ${isOpen ? "open" : ""}`}
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round">
        <path d={isOpen ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
      </svg>
    </button>

    {#if isOpen}
      <div class="dropdown-options">
        {#each tabs as tab}
          <div
            class="dropdown-option {isActive(tab) ? 'active' : ''}"
            on:click={() => selectOption(tab)}
            on:keydown={(e) => handleOptionKeyDown(e, tab)}
            tabindex="0"
            role="button"
            aria-label={getTabTitle(tab)}>
            {getTabTitle(tab)}
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <!-- Regular tab layout for larger screens -->
  <div class="tabs {_class}" role="tablist">
    {#each tabs as tab}
      <div
        role="presentation"
        class:active={isActive(tab)}
        class:useBorderedButtonContainer={tabStyles.useBorderedButton}>
        <a
          href={tab.link ? $reverse_router.routePage(tab.link) : tab.url}
          target={tab.link ? "_self" : "_blank"}
          class:active={isActive(tab)}
          class="tab-button {tabStyles.useBorderedButton ? 'useBorderedButton' : ''}"
          role="tab"
          aria-controls={`panel-${tab.id}`}
          id={`tab-${tab.id}`}>
          <span>{getTabTitle(tab)}</span>
        </a>
      </div>
    {/each}
  </div>
</div>

<style lang="scss">
  .tabs-container {
    margin: 0 auto;
    
    /* Hide active tab styles until mounted to prevent flash */
    &:not(.is-mounted) {
      div[role="presentation"].active .tab-button,
      div[role="presentation"].active.useBorderedButtonContainer .tab-button {
        background: transparent !important;
        color: var(--tab-font-color) !important;
        text-decoration: none !important;
      }
      
      .dropdown-option.active {
        background: transparent !important;
        color: var(--tab-font-color) !important;
      }
    }

    &.is-mounted {
      div[role="presentation"].active .tab-button,
      .dropdown-option.active {
        transition: background-color 0.2s ease, color 0.2s ease;
      }
    }
    
    @media (max-width: 950px) {
      width: 100%;
    }
  }

  .custom-dropdown-container {
    position: relative;
    display: none;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 950px) {
      display: block;
      max-width: 100%;
      padding: 0;
    }
  }

  .dropdown-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--tab-v-padding) var(--tab-h-padding);
    font-size: var(--tab-font-size);
    font-weight: 600;
    border: var(--tab-border-width) var(--tab-border-style) var(--tab-border-color);
    background: white;
    color: var(--tab-font-color);
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: var(--tab-border-radius);

    &:hover {
      border-color: var(--tab-border-color);
      color: var(--hover-tab-font-color);
    }
  }

  .dropdown-icon {
    transition: transform 0.2s ease;
    margin-left: 0.5rem;

    &.open {
      transform: rotate(180deg);
    }
  }

  .dropdown-options {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 300px;
    overflow-y: auto;
    background: white;
    border: var(--tab-border-width) var(--tab-border-style) var(--tab-border-color);
    margin-top: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 10;
    border-radius: var(--tab-border-radius);
    
    @media (max-width: 950px) {
      left: 0;
      right: 0;
      width: 100%
    }
  }

  .dropdown-option {
    padding: var(--tab-v-padding) var(--tab-h-padding);
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: var(--tab-font-size);
    color: var(--tab-font-color);

    &:hover {
      background: var(--hover-tab-color);
      color: var(--hover-tab-font-color);
    }

    &.active {
      background: var(--active-tab-color);
      color: var(--active-tab-font-color);
    }

    &:focus-visible {
      outline: 2px solid var(--tab-border-color);
      outline-offset: -2px;
    }
  }

  .tabs {
    display: grid;
    grid-template-columns: repeat(var(--number-of-columns), 1fr);
    gap: var(--tab-gap);
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 950px) {
    .tabs {
      display: none;
    }
  }

  .tab-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: var(--tab-v-padding) var(--tab-h-padding);
    border-radius: var(--tab-border-radius);
    width: 100%;
    cursor: pointer;
    text-decoration: none;
    color: var(--tab-font-color);
    font-weight: 600;
    font-size: var(--tab-font-size);
    letter-spacing: 0.05em;
    text-wrap: nowrap;

    &:hover {
      color: var(--hover-tab-font-color);
      text-decoration: underline;
    }
    
    &.active {
      color: var(--active-tab-font-color);
      text-decoration: underline;
    }
  }

  .useBorderedButton {
    background: white;
    border: var(--tab-border-width) var(--tab-border-style) var(--tab-border-color);
    transition: all 0.2s ease;

    &:hover {
      background: var(--hover-tab-color);
      color: var(--hover-tab-font-color);
      border-color: var(--tab-border-color);
      text-decoration: none;
    }
    
    &.active {
      background: var(--active-tab-color) !important;
      color: var(--active-tab-font-color) !important;
      text-decoration: none !important;
    }
  }
   
  div[role="presentation"].active.useBorderedButtonContainer {
    .tab-button {
      text-decoration: none;
      background: var(--active-tab-color);
      color: var(--active-tab-font-color);
    }
  }

  div[role="presentation"].active:not(.useBorderedButtonContainer) {
    .tab-button {
      text-decoration: underline;
      color: var(--active-tab-font-color);
    }
  }
</style>
